import { useNavigate } from "react-router-dom";
import { useCallback, useRef, useState, useEffect } from "react";
import { Upload as UploadIcon, FileArchive, Zap, Database, Cpu, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFiscalStore } from "@/store/fiscal-store";
import { startPipeline } from "@/lib/run-pipeline";
import { fmtBytes, fmtDur } from "@/lib/format";
import PipelineStatus from "@/components/PipelineStatus";
import { cn } from "@/lib/utils";

export default function UploadPage() {
  const navigate = useNavigate();
  const { state, fileName, fileSize, error, duracaoMs, dashboard } = useFiscalStore();
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = "Upload XML — Extrator Fiscal";
  }, []);

  const onFile = useCallback((file: File) => {
    const ext = file.name.toLowerCase();
    if (!ext.endsWith(".zip") && !ext.endsWith(".xml")) {
      alert("Envie um arquivo .xml ou um .zip contendo os XMLs.");
      return;
    }
    startPipeline(file);
  }, []);

  const busy = !["idle", "finished", "error"].includes(state);
  const showPipeline = state !== "idle";

  return (
    <div className="px-4 lg:px-8 py-4 lg:py-8 max-w-[1400px] mx-auto space-y-6 lg:space-y-8 animate-in fade-in duration-500">
      <header>
        <h1 className="text-xl lg:text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">Upload de Arquivos XML</h1>
        <p className="text-xs lg:text-sm text-slate-700 dark:text-slate-400 mt-1.5 leading-relaxed">
          Suporta múltiplos .xml ou .zip até 500 MB. Processamento em lotes de 5.000 arquivos.
        </p>
      </header>

      {/* Linha 1: Dropzone (esq) + Pipeline (dir) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            if (f) onFile(f);
          }}
          onClick={() => !busy && inputRef.current?.click()}
          className={cn(
            "relative rounded-3xl border-2 transition-all duration-500 p-6 lg:p-8 text-center cursor-pointer group overflow-hidden",
            "glass-card border-dashed min-h-[220px] lg:min-h-[260px] flex flex-col items-center justify-center",
            drag ? "border-primary border-solid bg-primary/5 shadow-glow scale-[1.01]" : "border-slate-300 dark:border-white/10 hover:border-primary/50",
            busy ? "opacity-40 pointer-events-none" : "hover:bg-white/5",
            "animate-in-card hover-lift"
          )}
        >
          <div className={cn(
            "size-14 rounded-2xl flex items-center justify-center shadow-xl transition-all duration-500",
            drag ? "bg-primary text-white" : "bg-white/80 dark:bg-white/5 border border-white/10 text-primary"
          )}>
            <UploadIcon className="size-6" />
          </div>

          <div className="relative z-10 mt-4">
            <h2 className="text-sm lg:text-base font-black tracking-tight text-slate-900 dark:text-white">
              Importação XML
            </h2>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
              Arraste pacotes <span className="text-primary font-bold">.ZIP</span> ou arquivos <span className="text-primary font-bold">.XML</span>.
            </p>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-1.5 relative z-10">
            <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-[8px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Max 500MB</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-[8px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Seguro</span>
          </div>
          
          <input
            ref={inputRef}
            type="file"
            accept=".zip,application/zip,.xml,text/xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = "";
            }}
          />

          {fileName && (
            <div className="mt-4 animate-in zoom-in-95 duration-300 w-full max-w-[220px]">
              <div className="flex items-center gap-2.5 rounded-xl bg-primary/10 border border-primary/20 p-1.5 shadow-lg backdrop-blur-xl">
                <div className="size-7 rounded-lg bg-primary flex items-center justify-center shrink-0">
                  <FileArchive className="size-3.5 text-white" />
                </div>
                <div className="text-left min-w-0 flex-1">
                  <div className="text-[9px] font-bold text-slate-900 dark:text-white truncate">{fileName}</div>
                  <div className="text-[8px] font-mono text-slate-500">{fmtBytes(fileSize)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 flex flex-col h-full justify-between">
          {showPipeline ? (
            <PipelineStatus />
          ) : (
            <div className="glass-card p-6 min-h-[160px] flex flex-col items-center justify-center text-center shadow-sm animate-in-card hover-glow" style={{ animationDelay: '150ms' }}>
              <div className="size-10 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-3">
                <Database className="size-5 text-slate-400 opacity-50" />
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 font-black">Motor Fiscal</div>
              <div className="mt-2 text-slate-700 dark:text-slate-300 text-xs font-medium">
                Aguardando entrada de dados…
              </div>
            </div>
          )}

          {state === "error" && (
            <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-xs text-destructive flex items-center gap-3 animate-in shake duration-500">
              <Zap className="size-4 shrink-0" />
              <span className="font-bold">Erro: {error}</span>
            </div>
          )}

          {state === "finished" && dashboard && (
            <div className="glass-card p-4 lg:p-5 border-emerald-500/20 bg-emerald-500/[0.02] relative overflow-hidden animate-in zoom-in-95 duration-500 hover-lift">
              <div className="absolute -right-12 -top-12 size-24 bg-emerald-500/10 blur-2xl rounded-full" />
              
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 relative z-10">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                    <CheckCircle2 className="size-5 text-emerald-500" />
                  </div>
                  <div>
                    <div className="text-[8px] font-black uppercase tracking-[0.2em] text-emerald-500 mb-0.5">Finalizado</div>
                    <div className="text-sm lg:text-base font-black text-slate-900 dark:text-white tabular-nums">
                      {dashboard.stats.totalNotas.toLocaleString("pt-BR")} <span className="text-[10px] font-bold opacity-60">Notas /</span> {fmtDur(duracaoMs)}
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate("/dashboard")} 
                  className="w-full sm:w-auto h-9 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg shadow-emerald-500/20 border-0 gap-2 transition-all hover:scale-105 active:scale-95 text-[10px] uppercase"
                >
                  Dashboard <ArrowRight className="size-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Linha 2: 3 cards de feature ultra-compactos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { i: Zap, t: "Streaming ZIP", d: "Leitura entry-by-entry via fflate.", delay: '300ms' },
          { i: Cpu, t: "Web Worker", d: "UI thread sempre livre.", delay: '400ms' },
          { i: Database, t: "Gestão Memória", d: "GC entre lotes (5.000 un).", delay: '500ms' },
        ].map((c) => (
          <div key={c.t} className="glass-card p-4 shadow-sm hover-lift animate-in-card flex items-center gap-4" style={{ animationDelay: c.delay }}>
            <div className="size-10 rounded-xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] flex items-center justify-center shadow-md shrink-0">
              <c.i className="size-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-xs lg:text-sm text-slate-900 dark:text-white truncate">{c.t}</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">{c.d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
